import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import { useDispatch } from 'react-redux';
import * as getArticleActions from '../../actions/getArticle';
import * as getArticlesActions from '../../actions/getArticles';
import selectArticle from '../../actions/selectArticle';
import mapDispatchToActions from '../../../utils/mapDispatchToActions';
/**
 * Provides access to all ghost actions for use in a React component.
 *
 * It automatically ensures that dispatch is mapped to each action.
 *
 * This also passes the GhostContentAPI to each action that requires it automatically, making them easier to use
 *
 *
 * ```js
 * import { ghost } from '@minerva-analytics/pantheon';
 *
 * const ghostApi = new GhostContentAPI({
 *   url: 'https://demo.ghost.io',
 *   key: 'abc123',
 *   version: "v3"
 * });
 *
 * const Test = () => {
 *   const actions = ghost.hooks.useGhostActions(ghostApi);
 *
 *   // use the actions as you need
 *
 *   return <div>Test</div>;
 * }
 *
 * console.log(useGhostActions(ghostApi));
 * {
 *   getArticle, // no need to provide the ghost api param to this action
 *   getArticleStart,
 *   getArticleSuccess,
 *   getArticleFailure,
 *   getArticles, // no need to provide the ghost api param to this action
 *   getArticlesStart,
 *   getArticlesSuccess,
 *   getArticlesFailure,
 *   selectArticle,
 * }
 * ```
 *
 * @param {GhostContentAPI} ghostApi - An initialized Ghost client library
 *    https://ghost.org/docs/api/v3/javascript/content/#working-example
 *
 * @returns {Object} - An object that holds all of the ghost actions
 */

var useGhostActions = function useGhostActions(ghostApi) {
  var dispatch = useDispatch();

  var _getArticle = getArticleActions.getArticle,
      otherArticleActions = _objectWithoutPropertiesLoose(getArticleActions, ["getArticle"]);

  var _getArticles = getArticlesActions.getArticles,
      otherArticlesActions = _objectWithoutPropertiesLoose(getArticlesActions, ["getArticles"]);

  return mapDispatchToActions(dispatch, _extends({
    getArticle: function getArticle() {
      for (var _len = arguments.length, rest = new Array(_len), _key = 0; _key < _len; _key++) {
        rest[_key] = arguments[_key];
      }

      return _getArticle.apply(void 0, [ghostApi].concat(rest));
    },
    getArticles: function getArticles() {
      for (var _len2 = arguments.length, rest = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        rest[_key2] = arguments[_key2];
      }

      return _getArticles.apply(void 0, [ghostApi].concat(rest));
    },
    selectArticle: selectArticle
  }, otherArticleActions, otherArticlesActions));
};

export default useGhostActions;